/**
 * Reconciliation orchestrator.
 *
 * Takes existing transactions + an incoming parsed statement,
 * runs matching and settlement detection, and returns a complete
 * ReconcileResult for the UI to display.
 *
 * Environment-agnostic: runs in WebWorker or server.
 */

import type {
  ExistingTransaction,
  ParsedStatement,
  ReconcileConfig,
  ReconcileResult,
} from "@paisa/types"
import { matchTransactions } from "./match"
import { detectSettlements } from "./settlement"

/**
 * Reconcile incoming statement transactions against existing transactions.
 *
 * Flow:
 * 1. Detect settlements in existing transactions (CC bill payments)
 * 2. Match incoming transactions against existing (exact → date tolerance → amount tolerance)
 * 3. Return matched, conflicts, settlements, and genuinely new transactions
 */
export function reconcile(
  existing: ExistingTransaction[],
  incoming: ParsedStatement,
  config?: ReconcileConfig,
): ReconcileResult {
  const accountLinks = config?.accountLinks ?? []

  // Step 1: Detect settlements among existing transactions (for display only —
  // settlements remain in the matching pool so re-imports are flagged correctly)
  const settlements = detectSettlements(existing, accountLinks)

  // Step 2: Match ALL existing against incoming — settlements stay in the pool
  // so re-importing the same statement correctly flags them as "Already in Paisa"
  const { matched, conflicts, unmatchedIndices } = matchTransactions(
    existing,
    incoming.transactions,
    {
      dateTolerance: config?.dateTolerance,
      amountTolerance: config?.amountTolerance,
    },
  )

  // Step 3: Collect unmatched incoming as new transactions
  const unmatched = unmatchedIndices.map((i) => incoming.transactions[i])

  return {
    matched,
    settlements,
    conflicts,
    unmatched,
    unmatchedIndices,
  }
}
